// FE-SCRM-057 — MarkResponseModal.
//
// Records the recipient's reaction to a sent proposal. Four
// post-send statuses (the lifecycle moves out of ``awaiting`` once
// the producer knows something) plus optional free-text notes.

import { useState } from "react";

import { Modal } from "@/design-system";

import type {
  MarkResponseInput, Proposal, ProposalResponseStatus,
} from "./types";


// ``awaiting`` is the default the proposal starts in — skipping it
// here so producers can only move forward through the lifecycle from
// this modal.
const RESPONSE_OPTIONS: readonly {
  value: ProposalResponseStatus; label: string;
}[] = [
  { value: "viewed", label: "Viewed" },
  { value: "feedback_received", label: "Feedback received" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
];


export interface MarkResponseModalProps {
  proposal: Proposal;
  isPending: boolean;
  onSubmit: (input: MarkResponseInput) => void;
  onClose: () => void;
}


export function MarkResponseModal({
  proposal, isPending, onSubmit, onClose,
}: MarkResponseModalProps) {
  const initial = (
    proposal.response_status === "awaiting"
      ? "viewed"
      : proposal.response_status
  ) as ProposalResponseStatus;
  const [status, setStatus] = useState<ProposalResponseStatus>(initial);
  const [notes, setNotes] = useState(proposal.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!status) {
      setError("Pick a response.");
      return;
    }
    setError(null);
    onSubmit({
      response_status: status,
      notes: notes.trim() ? notes.trim() : null,
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Mark v${proposal.version} response`}
      size="sm"
      footer={
        <>
          <button
            type="button"
            data-testid="proposal-mark-response-cancel"
            onClick={onClose}
            disabled={isPending}
            className={
              "rounded-md border border-border-subtle px-3 py-1.5 text-sm "
              + "text-text-primary hover:bg-recessed disabled:opacity-60"
            }
          >
            Cancel
          </button>
          <button
            type="submit"
            form="proposal-mark-response-form"
            data-testid="proposal-mark-response-submit"
            disabled={isPending}
            className={
              "rounded-md bg-teal px-3 py-1.5 text-sm font-medium "
              + "text-text-inverse hover:opacity-90 disabled:opacity-60"
            }
          >
            {isPending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <form
        id="proposal-mark-response-form"
        data-testid="proposal-mark-response-modal"
        onSubmit={handleSubmit}
        className="flex flex-col gap-3"
      >
        {error && (
          <div
            role="alert"
            data-testid="proposal-mark-response-error"
            className="rounded-md bg-ember/15 px-3 py-2 text-sm text-ember-ink"
          >
            {error}
          </div>
        )}

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-primary">Response</span>
          <select
            data-testid="proposal-mark-response-status"
            value={status}
            onChange={
              (e) => setStatus(e.target.value as ProposalResponseStatus)
            }
            className={
              "rounded-md border border-border-subtle bg-raised px-2 "
              + "py-1.5 text-sm"
            }
          >
            {RESPONSE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-primary">Notes</span>
          <textarea
            data-testid="proposal-mark-response-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="e.g. CFO loved the renewal math; legal review next."
            className={
              "rounded-md border border-border-subtle bg-raised px-2 "
              + "py-1.5 text-sm"
            }
          />
        </label>
      </form>
    </Modal>
  );
}
